import type { CustomFieldInputType } from "@/api/db/connection";
import type { DocumentBlock, InlineNode, TreeNode } from "@/api/document/block";
import { CustomFieldValues } from "@/api/document/custom-field-values";

interface PatternField {
	id: number;
	label: string;
	input_type: CustomFieldInputType;
	pattern_id: number | null;
}

export interface DocumentPatternItem {
	id: string;
	values: Map<number, string>;
}

export type DocumentPatternValueMap = Map<number, DocumentPatternItem[]>;

function boundInlines(input: {
	content: InlineNode[] | undefined;
	patternId: number;
	item: DocumentPatternItem;
	fields: Map<number, PatternField>;
	mode: "preview" | "resolved";
}) {
	return input.content?.map((inline): InlineNode => {
		if (inline.type !== "field") {
			return inline;
		}

		const field = input.fields.get(inline.attrs.fieldId);

		if (field?.pattern_id !== input.patternId) {
			return inline;
		}

		const value = input.item.values.get(field.id);

		if (value === undefined) {
			if (input.mode === "resolved") {
				throw new Error(`unresolved_pattern_field:${input.patternId}:${field.id}`);
			}

			return { type: "text", text: field.label };
		}

		return { type: "text", text: CustomFieldValues.format(field.input_type, value) };
	});
}

function cloneNode(input: {
	node: Exclude<TreeNode, { type: "page" | "pattern" }>;
	patternId: number;
	item: DocumentPatternItem;
	fields: Map<number, PatternField>;
	mode: "preview" | "resolved";
}): Exclude<TreeNode, { type: "page" | "pattern" }> {
	const id = `${input.node.attrs.id}:${input.item.id}`;
	const inlines = (content: InlineNode[] | undefined) =>
		boundInlines({
			content,
			patternId: input.patternId,
			item: input.item,
			fields: input.fields,
			mode: input.mode,
		});
	const nested = <T extends Exclude<TreeNode, { type: "page" | "pattern" }>>(node: T) =>
		cloneNode({ ...input, node }) as T;

	switch (input.node.type) {
		case "heading":
			return {
				...input.node,
				attrs: { ...input.node.attrs, id },
				content: inlines(input.node.content),
			};
		case "paragraph":
			return {
				...input.node,
				attrs: { ...input.node.attrs, id },
				content: inlines(input.node.content),
			};
		case "list-item":
			return { ...input.node, attrs: { id }, content: inlines(input.node.content) };
		case "step":
			return {
				...input.node,
				attrs: { ...input.node.attrs, id },
				content: inlines(input.node.content),
			};
		case "table-cell":
			return { ...input.node, attrs: { id }, content: inlines(input.node.content) };
		case "table-row":
			return { ...input.node, attrs: { id }, content: input.node.content.map(nested) };
		case "table":
			return { ...input.node, attrs: { id }, content: input.node.content.map(nested) };
		case "variant-card":
			return {
				...input.node,
				attrs: { ...input.node.attrs, id },
				content: input.node.content.map(nested),
			};
		case "signature-slot":
			return { ...input.node, attrs: { id }, content: inlines(input.node.content) };
		case "signature-row":
			return { ...input.node, attrs: { id }, content: input.node.content.map(nested) };
		case "signature":
			return { ...input.node, attrs: { id }, content: input.node.content.map(nested) };
		case "page-break":
			return { ...input.node, attrs: { id } };
		case "image":
			return { ...input.node, attrs: { ...input.node.attrs, id } };
	}
}

function clonePatternChild(input: {
	node: Extract<DocumentBlock, { type: "pattern" }>["content"][number];
	patternId: number;
	item: DocumentPatternItem;
	fields: Map<number, PatternField>;
	mode: "preview" | "resolved";
}) {
	return cloneNode(input) as Extract<DocumentBlock, { type: "pattern" }>["content"][number];
}

export const DocumentPatterns = {
	remap(blocks: DocumentBlock[], ids: Map<number, number>) {
		return blocks.map((block) => {
			if (block.type !== "pattern") {
				return block;
			}

			const patternId = ids.get(block.attrs.patternId);

			if (patternId === undefined) {
				return block;
			}

			return { ...block, attrs: { ...block.attrs, patternId } };
		});
	},

	referenced(blocks: DocumentBlock[]) {
		return new Set(
			blocks.filter((block) => block.type === "pattern").map((block) => block.attrs.patternId),
		);
	},

	materialize(input: {
		blocks: DocumentBlock[];
		fields: Map<number, PatternField>;
		values: DocumentPatternValueMap;
		mode: "preview" | "resolved";
	}) {
		const blocks: DocumentBlock[] = [];

		for (const block of input.blocks) {
			if (block.type !== "pattern") {
				blocks.push(block);

				continue;
			}

			for (const item of input.values.get(block.attrs.patternId) ?? []) {
				for (const child of block.content) {
					blocks.push(
						clonePatternChild({
							node: child,
							patternId: block.attrs.patternId,
							item,
							fields: input.fields,
							mode: input.mode,
						}),
					);
				}
			}
		}

		return blocks;
	},
};
